/**
 * Per-entity toggle generation guard for optimistic like/follow toggles.
 *
 * Each rapid toggle of an entity bumps that entity's counter and captures the
 * value; a slow-failing mutation's revert must fire ONLY while its generation
 * is still the latest. Without this, an older toggle that fails last would
 * clobber the overlay a newer toggle already moved on to (e.g. like→unlike→
 * like, then the first like's write fails after the rest have settled).
 *
 * Each caller creates its OWN registry so entity-id namespaces stay isolated —
 * a track id and an album id that happen to collide never share a counter.
 */
export interface ToggleGeneration {
    /** Bump `id`'s counter and return the now-current generation. */
    nextGeneration(id: string): number
    /** True once a newer toggle has superseded `gen` for `id`. */
    isStaleGeneration(id: string, gen: number): boolean
}

export function createToggleGeneration(): ToggleGeneration {
    const generations = new Map<string, number>()
    return {
        nextGeneration(id) {
            const gen = (generations.get(id) ?? 0) + 1
            generations.set(id, gen)
            return gen
        },
        isStaleGeneration(id, gen) {
            return generations.get(id) !== gen
        },
    }
}

export interface OptimisticToggleParams<T> {
    readonly toggleGeneration: ToggleGeneration
    readonly id: string
    /** Write the toggled-to overlay. */
    readonly applyNext: () => void
    /** Restore the pre-toggle overlay. */
    readonly applyPrev: () => void
    /** Fire the server write. */
    readonly save: () => Promise<T>
}

/**
 * Bump `id`'s generation, apply the optimistic overlay, then fire `save`. On
 * failure the pre-toggle overlay is restored ONLY while this toggle is still
 * the latest — a stale (superseded) failure no-ops so it can't clobber the
 * overlay a newer toggle already moved on to. The rejection always rethrows.
 */
export function runOptimisticToggle<T>({
    toggleGeneration,
    id,
    applyNext,
    applyPrev,
    save,
}: OptimisticToggleParams<T>): Promise<T> {
    const generation = toggleGeneration.nextGeneration(id)
    applyNext()
    return save().catch((err) => {
        if (!toggleGeneration.isStaleGeneration(id, generation)) applyPrev()
        throw err
    })
}
